import { Component, ElementRef, OnDestroy, OnInit, afterNextRender, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { I18nService } from '../../../../Shared/i18n/i18n.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomerAuthService } from '../../data/customer-auth.service';
import { SocialAuthService } from '../../data/social-auth.service';
import { PhoneFieldComponent } from '../../../../Shared/phone/phone-field';

/**
 * Shared sign-in UI (the foodpanda-style "Welcome!" panel): Continue with Google / Facebook plus an
 * email one-time-code flow. Presentational + self-contained — it owns the auth calls and emits
 * {@link authenticated} on success; the host (modal or /login page) decides where to go next.
 *
 * Social buttons appear only when their provider is configured (SocialAuthService). Zoneless: all
 * async state lives in signals so the view updates without zone.js.
 */
@Component({
  selector: 'app-auth-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, PhoneFieldComponent],
  templateUrl: './auth-panel.html',
  styleUrls: ['./auth-panel.css'],
})
export class AuthPanelComponent implements OnInit, OnDestroy {
  protected readonly i18n = inject(I18nService);
  /** Emitted once a session has been established (token stored in CustomerAuthService). */
  readonly authenticated = output<void>();

  /** Optional starting screen (e.g. a "Sign up" CTA opens this panel straight on signup). */
  readonly initialMode = input<'welcome' | 'login' | 'signup'>('welcome');

  ngOnInit(): void {
    const m = this.initialMode();
    if (m !== 'welcome') this.mode.set(m);
  }

  protected readonly social = inject(SocialAuthService);
  private readonly auth = inject(CustomerAuthService);

  protected readonly mode = signal<'welcome' | 'code' | 'login' | 'signup' | 'verify' | 'forgot' | 'forgot-verify' | 'forgot-reset'>('welcome');
  protected email = '';
  protected code = '';
  protected displayName = '';
  protected password = '';
  protected confirmPassword = '';
  protected newPassword = '';
  /** Reset-password confirmation. Separate from confirmPassword, which belongs to the sign-up form. */
  protected confirmNewPassword = '';
  /** E.164 or '' — written by app-phone-field, which never publishes an incomplete number. */
  protected phone = '';
  /** Surfaces the phone field's error after a failed submit, before the box has been blurred. */
  protected phoneTouched = false;
  protected readonly loading = signal(false);      // email request/verify in flight
  protected readonly socialLoading = signal(false); // Google/Facebook exchange in flight
  protected readonly error = signal('');
  protected readonly info = signal('');
  /** Seconds until the sign-up verification code can be re-sent. */
  protected readonly resendIn = signal(0);
  private resendTimer: ReturnType<typeof setInterval> | null = null;

  protected readonly showSocial = computed(() => this.social.googleEnabled || this.social.facebookEnabled);

  private readonly googleBtn = viewChild<ElementRef<HTMLElement>>('googleBtn');
  private readonly phoneField = viewChild(PhoneFieldComponent);

  constructor() {
    // Render Google's button once the view exists (browser-only via afterNextRender).
    afterNextRender(() => {
      const el = this.googleBtn()?.nativeElement;
      if (el) {
        this.social
          .renderGoogleButton(el, (idToken) => this.onGoogleCredential(idToken))
          // SDK blocked/unavailable: leave the area empty — email + Facebook remain usable.
          .catch(() => {});
      }
    });
  }

  sendEmailCode(): void {
    this.error.set('');
    const email = this.email.trim();
    if (!this.isValidEmail(email)) {
      this.error.set('Enter a valid email address.');
      return;
    }
    this.loading.set(true);
    this.auth.requestEmailOtp(email).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) {
          this.mode.set('code');
          this.info.set('We emailed you a 6-digit code.');
        } else {
          this.error.set(res.message || 'Could not send the code.');
        }
      },
      error: (e) => {
        this.loading.set(false);
        this.error.set(e?.error?.message || 'Could not send the code. Please try again.');
      },
    });
  }

  verifyEmailCode(): void {
    this.error.set('');
    const code = this.code.trim();
    if (!code) {
      this.error.set('Enter the code we emailed you.');
      return;
    }
    this.loading.set(true);
    this.auth.verifyEmailOtp(this.email.trim(), code).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) this.authenticated.emit();
        else this.error.set(res.message || 'Incorrect code.');
      },
      error: (e) => {
        this.loading.set(false);
        this.error.set(e?.error?.message || 'Incorrect code. Please try again.');
      },
    });
  }

  changeEmail(): void {
    this.mode.set('welcome');
    this.code = '';
    this.error.set('');
    this.info.set('');
  }

  // ── Email + password ──
  showSignup(): void { this.mode.set('signup'); this.error.set(''); this.info.set(''); }
  showLogin(): void { this.mode.set('login'); this.error.set(''); this.info.set(''); }
  backToWelcome(): void {
    this.mode.set('welcome');
    this.password = ''; this.confirmPassword = ''; this.error.set(''); this.info.set('');
  }

  doSignup(): void {
    this.error.set('');
    const name = this.displayName.trim();
    const email = this.email.trim();
    if (!name) { this.error.set('Enter your name.'); return; }
    if (!this.isValidEmail(email)) { this.error.set('Enter a valid email address.'); return; }
    if (!this.password || this.password.length < 6) { this.error.set('Password must be at least 6 characters.'); return; }
    // Block on mismatch — the inline hint under the confirm-password field is the (visible) feedback.
    if (this.password !== this.confirmPassword) { return; }
    // The phone is optional, so a blank one is fine — but a half-typed one must not be waved through.
    // The field reports '' until the number is complete, so without this the shopper would be signed
    // up with no number at all and never told that what they typed was discarded.
    if (this.phoneField()?.partial()) { this.phoneTouched = true; return; }
    // Prove the email before creating the account: the account only exists once the emailed code
    // checks out (see verifySignupCode), so an unverified email never gets a session.
    this.loading.set(true);
    this.auth.requestSignupOtp(email).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) {
          this.code = '';
          this.mode.set('verify');
          this.info.set(`We emailed a 6-digit code to ${email}.`);
          this.startResendCooldown();
        } else this.error.set(res.message || 'Could not send the verification code.');
      },
      error: (e) => { this.loading.set(false); this.error.set(e?.error?.message || 'Could not send the verification code. Please try again.'); },
    });
  }

  /** Email address whose sign-up code already checked out — lets a retry after a failed
   *  register call skip re-verifying the (consumed) code. */
  private signupVerifiedEmail = '';

  /** Finish signup: verify the emailed code, then create the account. */
  verifySignupCode(): void {
    this.error.set('');
    const name = this.displayName.trim();
    const email = this.email.trim();
    // Already proven (e.g. the register step failed and the user retries) — go straight to register;
    // the server honours the consumed challenge for 30 minutes.
    if (this.signupVerifiedEmail === email) {
      this.loading.set(true);
      this.createAccount(email, name);
      return;
    }
    const code = this.code.trim();
    if (!code) { this.error.set('Enter the verification code.'); return; }
    this.loading.set(true);
    this.auth.verifySignupOtp(email, code).subscribe({
      next: (res) => {
        if (!res.success) {
          this.loading.set(false);
          this.error.set(res.message || 'Incorrect code.');
          return;
        }
        this.signupVerifiedEmail = email;
        this.createAccount(email, name);
      },
      error: (e) => { this.loading.set(false); this.error.set(e?.error?.message || 'Incorrect code. Please try again.'); },
    });
  }

  /** Email ownership proven — create the account (registers verified and starts a session). */
  private createAccount(email: string, name: string): void {
    this.auth.register({ email, password: this.password, confirmPassword: this.confirmPassword, phone: this.phone.trim() || null, displayName: name }).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) this.authenticated.emit();
        else this.error.set(res.message || 'Could not create your account.');
      },
      error: (e) => { this.loading.set(false); this.error.set(e?.error?.message || 'Could not send the verification code. Please try again.'); },
    });
  }

  /** Re-send the sign-up verification code (rate-limited by the cooldown + the API's throttle). */
  resendSignupCode(): void {
    if (this.resendIn() > 0) return;
    this.error.set('');
    const email = this.email.trim();
    this.loading.set(true);
    this.auth.requestSignupOtp(email).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) {
          this.info.set(`We emailed a new code to ${email}.`);
          this.startResendCooldown();
        } else this.error.set(res.message || 'Could not send the verification code.');
      },
      error: (e) => { this.loading.set(false); this.error.set(e?.error?.message || 'Could not send the verification code. Please try again.'); },
    });
  }

  /** Back from the verify step to the signup form (e.g. to fix the email). */
  backToSignup(): void {
    this.mode.set('signup');
    this.code = '';
    this.error.set('');
    this.info.set('');
  }

  private startResendCooldown(seconds = 45): void {
    this.clearResendTimer();
    this.resendIn.set(seconds);
    this.resendTimer = setInterval(() => {
      const n = this.resendIn() - 1;
      this.resendIn.set(n > 0 ? n : 0);
      if (n <= 0) this.clearResendTimer();
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimer) { clearInterval(this.resendTimer); this.resendTimer = null; }
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
  }

  // ── Forgot password (email OTP) ──
  showForgot(): void {
    this.mode.set('forgot');
    this.code = ''; this.newPassword = ''; this.confirmNewPassword = ''; this.error.set(''); this.info.set('');
  }

  sendResetCode(): void {
    this.error.set('');
    const email = this.email.trim();
    if (!this.isValidEmail(email)) { this.error.set('Enter a valid email address.'); return; }
    this.loading.set(true);
    this.auth.requestPasswordReset(email).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) {
          this.mode.set('forgot-verify');
          this.info.set('If an account exists for that email, we’ve sent a reset code.');
        } else this.error.set(res.message || 'Could not send a reset code.');
      },
      error: (e) => { this.loading.set(false); this.error.set(e?.error?.message || 'Could not send a reset code.'); },
    });
  }

  /** Step 1 of reset: verify the emailed code, then reveal the new-password step. */
  verifyResetCode(): void {
    this.error.set('');
    const code = this.code.trim();
    if (!code) { this.error.set('Enter the code we emailed you.'); return; }
    this.loading.set(true);
    this.auth.verifyResetCode(this.email.trim(), code).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) {
          this.mode.set('forgot-reset');
          this.info.set('Code verified — choose your new password.');
        } else this.error.set(res.message || 'Incorrect code. Please try again.');
      },
      error: (e) => { this.loading.set(false); this.error.set(e?.error?.message || 'Incorrect code. Please try again.'); },
    });
  }

  /** Step 2 of reset: set the new password (re-verifies + consumes the code server-side). */
  resetPassword(): void {
    this.error.set('');
    const code = this.code.trim();
    if (!code) { this.error.set('Enter the code we emailed you.'); this.mode.set('forgot-verify'); return; }
    if (!this.newPassword || this.newPassword.length < 6) { this.error.set('Password must be at least 6 characters.'); return; }
    // Checked BEFORE the request: the reset code is single-use, so letting a mistyped password through
    // would burn the code and leave the customer asking for a fresh email to fix a typo.
    if (this.newPassword !== this.confirmNewPassword) { this.error.set(this.i18n.t('auth.passwordsMismatch')); return; }
    this.loading.set(true);
    this.auth.resetPassword(this.email.trim(), code, this.newPassword).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) {
          this.password = this.newPassword;
          this.newPassword = '';
          this.confirmNewPassword = '';
          this.mode.set('login');
          this.info.set('Password updated. Sign in with your new password.');
        } else this.error.set(res.message || 'Could not reset your password.');
      },
      error: (e) => { this.loading.set(false); this.error.set(e?.error?.message || 'Could not reset your password.'); },
    });
  }

  doLogin(): void {
    this.error.set('');
    const email = this.email.trim();
    if (!this.isValidEmail(email) || !this.password) { this.error.set('Enter your email and password.'); return; }
    this.loading.set(true);
    this.auth.login(email, this.password).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.success) this.authenticated.emit();
        else this.error.set(res.message || 'Incorrect email or password.');
      },
      error: (e) => { this.loading.set(false); this.error.set(e?.error?.message || 'Incorrect email or password.'); },
    });
  }

  continueWithFacebook(): void {
    this.error.set('');
    this.socialLoading.set(true);
    this.social
      .facebookLogin()
      .then((token) =>
        this.auth.facebookSignIn(token).subscribe({
          next: (res) => {
            this.socialLoading.set(false);
            if (res.success) this.authenticated.emit();
            else this.error.set(res.message || 'Facebook sign-in failed.');
          },
          error: (e) => {
            this.socialLoading.set(false);
            this.error.set(e?.error?.message || 'Facebook sign-in failed.');
          },
        }),
      )
      .catch(() => {
        // User closed the dialog / cancelled — not an error worth shouting about.
        this.socialLoading.set(false);
      });
  }

  private onGoogleCredential(idToken: string): void {
    this.error.set('');
    this.socialLoading.set(true);
    this.auth.googleSignIn(idToken).subscribe({
      next: (res) => {
        this.socialLoading.set(false);
        if (res.success) this.authenticated.emit();
        else this.error.set(res.message || 'Google sign-in failed.');
      },
      error: (e) => {
        this.socialLoading.set(false);
        this.error.set(e?.error?.message || 'Google sign-in failed.');
      },
    });
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}
